// src/pages/UserPage/sections/OrganizationDeliveries/dashboard/SenderModal.jsx
import React, { useState } from 'react';
import {
  Box, Typography, Chip, Stack, IconButton, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell, Checkbox,
  CircularProgress, Tooltip,
} from '@mui/material';
import { X, Send, Edit, Check } from 'lucide-react';
import { invoiceSelectedDeliveriesFn } from '../../../../../firebase/deliveryFunctions';
import { BILLING_STATUS, getBillingStatus, fmtDateOnly } from './constants';

function fmtEur(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)} €`;
}

export const SenderModal = ({ group, organizationId, onClose, onStatusUpdate }) => {
  const [selected, setSelected] = useState(() => new Set(group.deliveries.map((d) => d.id)));
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingCompanyName, setBillingCompanyName] = useState(group.companyName || '');
  const [billingYNumber, setBillingYNumber]         = useState(group.yNumber || '');
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState(null);

  const toggleRow = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(selected.size === group.deliveries.length
      ? new Set()
      : new Set(group.deliveries.map((d) => d.id))
    );

  const selectedDeliveries = group.deliveries.filter((d) => selected.has(d.id));
  const selectedTotal      = selectedDeliveries.reduce((s, d) => s + (Number(d.price) || 0), 0);
  const effectiveYNumber   = billingYNumber.trim();
  const canSend            = selected.size > 0 && !!effectiveYNumber;

  const netvisorTooltip = !effectiveYNumber
    ? 'Y-tunnus puuttuu — muokkaa laskutustietoja ennen lähetystä'
    : selected.size === 0
    ? 'Valitse vähintään yksi toimitus'
    : `Lähetetään ${selected.size} toimitusta Netvisoriin`;

  const handleSendNetvisor = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const result = await invoiceSelectedDeliveriesFn(
        organizationId,
        selectedDeliveries.map((d) => d.id),
      );
      const { invoicesSent = 0, invoicesFailed = 0 } = result || {};

      if (invoicesFailed > 0) {
        setError(`${invoicesSent} lähetetty, ${invoicesFailed} epäonnistui`);
      }
      if (invoicesSent > 0) {
        onStatusUpdate?.(new Set(selectedDeliveries.map((d) => d.id)), 'invoiced');
        onClose();
      }
    } catch (err) {
      console.error(err);
      setError(`Netvisor-lähetys epäonnistui: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ flex: 1, mr: 2 }}>
            {editingBilling ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <TextField
                  label="Yritys" value={billingCompanyName}
                  onChange={(e) => setBillingCompanyName(e.target.value)}
                  size="small" sx={{ width: 200 }}
                />
                <TextField
                  label="Y-tunnus" value={billingYNumber}
                  onChange={(e) => setBillingYNumber(e.target.value)}
                  size="small" sx={{ width: 140 }}
                />
                <IconButton size="small" color="primary" onClick={() => setEditingBilling(false)}>
                  <Check size={16} />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                    {billingCompanyName || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {effectiveYNumber || (
                      <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>
                        Y-tunnus puuttuu
                      </Box>
                    )}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setEditingBilling(true)}>
                  <Edit size={15} />
                </IconButton>
              </Stack>
            )}
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" flexShrink={0}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {selected.size} / {group.deliveries.length} valittu
              </Typography>
              <Typography variant="body2" fontWeight={700}>{fmtEur(selectedTotal)}</Typography>
            </Box>
            <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mt: 1 }}>
        {error && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="error">{error}</Typography>
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={selected.size === group.deliveries.length}
                  indeterminate={selected.size > 0 && selected.size < group.deliveries.length}
                  onChange={toggleAll}
                />
              </TableCell>
              {['Toimitus ID', 'Pvm', 'Lähtö', 'Määränpää', 'Hinta', 'Tila'].map((h) => (
                <TableCell key={h} sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {group.deliveries.map((d) => {
              const statusInfo = BILLING_STATUS[getBillingStatus(d)] || BILLING_STATUS.pending;
              const isSelected = selected.has(d.id);
              return (
                <TableRow key={d.id} hover selected={isSelected}
                  onClick={() => toggleRow(d.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={isSelected}
                      onChange={() => toggleRow(d.id)}
                      onClick={(e) => e.stopPropagation()} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" fontWeight={500}>
                      {d.deliveryId || d.id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{fmtDateOnly(d.completedAtMs)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.origin || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.destination || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }} align="right">
                    {fmtEur(d.price)}
                  </TableCell>
                  <TableCell>
                    <Chip label={statusInfo.label} color={statusInfo.color}
                      size="small" variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} size="small" disabled={sending}>Sulje</Button>
        <Tooltip title={netvisorTooltip}>
          <span>
            <Button
              variant="contained" size="small" color="primary"
              startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}
              onClick={handleSendNetvisor}
              disabled={!canSend || sending}
            >
              {sending ? 'Lähetetään...' : `Lähetä Netvisoriin (${selected.size})`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SenderModal;
